import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, Component, Suspense, lazy } from 'react';
import { useAuth } from '@/context/AuthContext';
import { OrgProvider } from '@/context/OrgContext';
import { TooltipProvider } from '@/components/ui/tooltip';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { OrgRoute, OrgAdminRoute, OrgOwnerRoute } from '@/components/OrgRoute';

// Auth Pages
import Login from '@/pages/Login';
import AdminLogin from '@/pages/AdminLogin';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import AcceptInvite from '@/pages/AcceptInvite';
import InviteSetup from '@/pages/InviteSetup';

// Landing Page
import LandingPage from '@/pages/LandingPage';

// Legal pages
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import SecurityPage from '@/pages/SecurityPage';

// Info pages
import HelpCenter from '@/pages/HelpCenter';
import APIDocs from '@/pages/APIDocs';
import StatusPage from '@/pages/StatusPage';

// Admin Pages (lazy loaded for code splitting)
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Calendars = lazy(() => import('@/pages/Calendars'));
const CalendarCreate = lazy(() => import('@/pages/CalendarCreate'));
const CalendarEdit = lazy(() => import('@/pages/CalendarEdit'));
const AppointmentsCalendar = lazy(() => import('@/pages/AppointmentsCalendar'));
const Clients = lazy(() => import('@/pages/Clients'));
const ClientDetail = lazy(() => import('@/pages/ClientDetail'));
const Team = lazy(() => import('@/pages/Team'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const Settings = lazy(() => import('@/pages/Settings'));
const Guide = lazy(() => import('@/pages/Guide'));
const OrganizationPage = lazy(() => import('@/pages/Organization'));
const Billing = lazy(() => import('@/pages/Billing'));
const EmailTemplates = lazy(() => import('@/pages/EmailTemplates'));
const Personalization = lazy(() => import('@/pages/Personalization'));
const POSDashboard = lazy(() => import('@/pages/POSDashboard'));
const TaxSettings = lazy(() => import('@/pages/TaxSettings'));
const Forecasting = lazy(() => import('@/pages/Forecasting'));
const Reviews = lazy(() => import('@/pages/Reviews'));
const ResellerProgram = lazy(() => import('@/pages/ResellerProgram'));

// Platform Admin Pages (lazy loaded)
const AdminLayout = lazy(() => import('@/components/layout/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const AdminOrganizations = lazy(() => import('@/pages/admin/Organizations'));
const AdminUsers = lazy(() => import('@/pages/admin/Users'));
const AdminSubscriptions = lazy(() => import('@/pages/admin/Subscriptions'));
const AdminSupport = lazy(() => import('@/pages/admin/Support'));
const AdminAnalytics = lazy(() => import('@/pages/admin/Analytics'));
const AdminReviews = lazy(() => import('@/pages/admin/Reviews'));
const AdminNetwork = lazy(() => import('@/pages/admin/AdminNetwork'));
const AdminErrors = lazy(() => import('@/pages/admin/AdminErrors'));
const AdminActivityLog = lazy(() => import('@/pages/admin/AdminActivityLog'));
const AdminSecurity = lazy(() => import('@/pages/admin/AdminSecurity'));
const AdminProfile = lazy(() => import('@/pages/admin/AdminProfile'));
const AdminStorage = lazy(() => import('@/pages/admin/AdminStorage'));
const AdminDatabase = lazy(() => import('@/pages/admin/AdminDatabase'));
const AdminTraffic = lazy(() => import('@/pages/admin/AdminTraffic'));
const AdminResources = lazy(() => import('@/pages/admin/AdminResources'));
const AdminApiKeys = lazy(() => import('@/pages/admin/AdminApiKeys'));
const AdminWebhooks = lazy(() => import('@/pages/admin/AdminWebhooks'));
const AdminIntegrations = lazy(() => import('@/pages/admin/AdminIntegrations'));
const AdminBackup = lazy(() => import('@/pages/admin/AdminBackup'));
const AdminFeatureFlags = lazy(() => import('@/pages/admin/AdminFeatureFlags'));
const AdminNotifications = lazy(() => import('@/pages/admin/AdminNotifications'));
const AdminAuditLogs = lazy(() => import('@/pages/admin/AdminAuditLogs'));
const AdminReports = lazy(() => import('@/pages/admin/AdminReports'));
const AdminMaintenance = lazy(() => import('@/pages/admin/AdminMaintenance'));
const AdminGDPR = lazy(() => import('@/pages/admin/AdminGDPR'));
const AdminLogsViewer = lazy(() => import('@/pages/admin/AdminLogsViewer'));
const AdminCronJobs = lazy(() => import('@/pages/admin/AdminCronJobs'));
const AdminCache = lazy(() => import('@/pages/admin/AdminCache'));
const AdminEmailDelivery = lazy(() => import('@/pages/admin/AdminEmailDelivery'));
const AdminUserRoles = lazy(() => import('@/pages/admin/AdminUserRoles'));
const AdminBillingInvoices = lazy(() => import('@/pages/admin/AdminBillingInvoices'));
const AdminLocalization = lazy(() => import('@/pages/admin/AdminLocalization'));
const AdminSettings = lazy(() => import('@/pages/admin/Settings'));

// Public Pages (lazy loaded)
const BookingPage = lazy(() => import('@/pages/public/BookingPage'));
const BookingRegister = lazy(() => import('@/pages/public/BookingRegister'));
const BookingConfirm = lazy(() => import('@/pages/public/BookingConfirm'));
const BookingManage = lazy(() => import('@/pages/public/BookingManage'));
const ReviewPage = lazy(() => import('@/pages/public/ReviewPage'));
const PublicProfilePage = lazy(() => import('@/pages/public/PublicProfilePage'));
const MarketplacePage = lazy(() => import('@/pages/public/MarketplacePage'));

// Org Panel (lazy loaded)
const OrgPanel = lazy(() => import('@/org-panel/OrgPanel'));
const OrgMembers = lazy(() => import('@/org-panel/Members'));
const OrgCalendars = lazy(() => import('@/org-panel/Calendars'));
const OrgCalendarCreate = lazy(() => import('@/org-panel/CalendarCreate'));
const OrgCalendarEdit = lazy(() => import('@/org-panel/CalendarEdit'));
const OrgAppointments = lazy(() => import('@/org-panel/Appointments'));
const OrgClients = lazy(() => import('@/org-panel/Clients'));
const OrgClientDetail = lazy(() => import('@/org-panel/ClientDetail'));
const OrgEmailTemplates = lazy(() => import('@/org-panel/EmailTemplates'));
const OrgPersonalization = lazy(() => import('@/org-panel/Personalization'));
const OrgPOS = lazy(() => import('@/org-panel/POS'));
const OrgTransactions = lazy(() => import('@/org-panel/Transactions'));
const OrgTaxSettings = lazy(() => import('@/org-panel/TaxSettings'));
const OrgForecasting = lazy(() => import('@/org-panel/Forecasting'));
const OrgReseller = lazy(() => import('@/org-panel/Reseller'));
const OrgReviews = lazy(() => import('@/org-panel/Reviews'));
const OrgGuide = lazy(() => import('@/org-panel/Guide'));
const OrgTeam = lazy(() => import('@/org-panel/Team'));
const OrgOrganizationSettings = lazy(() => import('@/org-panel/OrganizationSettings'));
const OrgUserProfile = lazy(() => import('@/org-panel/UserProfile'));
const OrgBilling = lazy(() => import('@/org-panel/Billing'));
const OrgAnalytics = lazy(() => import('@/org-panel/Analytics'));
const OrgIntegrations = lazy(() => import('@/org-panel/Integrations'));
const OrgSettings = lazy(() => import('@/org-panel/Settings'));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return user ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" />;
  if (!user.isSuperAdmin && user.role !== 'owner') return <Navigate to="/admin/login" />;
  return children;
}

function ThemeInitializer() {
  useEffect(() => {
    try {
      const theme = localStorage.getItem('theme');
      if (theme) {
        document.documentElement.classList.remove('light', 'dark');
        document.documentElement.classList.add(theme);
      }
    } catch {}
  }, []);
  return null;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return user ? <Navigate to="/org/overview" /> : children;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center space-y-4 max-w-md">
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="text-muted-foreground">Please refresh the page or try again later.</p>
            {this.state.error?.message && (
              <pre className="text-xs text-left bg-muted p-3 rounded-lg overflow-auto max-h-40 text-muted-foreground">{this.state.error.message}</pre>
            )}
            <div className="flex gap-3 justify-center">
              <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">Reload</button>
              <button onClick={() => this.setState({ hasError: false, error: null })} className="px-4 py-2 rounded-lg border border-border">Try Again</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="text-muted-foreground">Page not found</p>
        <a href="/" className="text-primary hover:underline">Go home</a>
      </div>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <TooltipProvider delayDuration={300}>
        <ThemeInitializer />
        <Suspense fallback={<PageLoader />}>
          <Routes>
        {/* Landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Legal pages */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/security" element={<SecurityPage />} />

        {/* Info pages */}
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/api-docs" element={<APIDocs />} />
        <Route path="/status" element={<StatusPage />} />

        {/* Public booking pages */}
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/book/:slug/register" element={<BookingRegister />} />
        <Route path="/book/:slug/confirm" element={<BookingConfirm />} />
        <Route path="/manage-booking/:token" element={<BookingManage />} />
        <Route path="/booking-success" element={<BookingConfirm />} />
        <Route path="/booking-cancelled" element={<BookingPage />} />
        <Route path="/review/:token" element={<ReviewPage />} />
        <Route path="/profile/:userId" element={<PublicProfilePage />} />
        <Route path="/discover" element={<MarketplacePage />} />
        <Route path="/staff/:staffId/:slug" element={<BookingPage />} />

        {/* Auth */}
        <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
        <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/accept-invite/:token" element={<AcceptInvite />} />
        <Route path="/invite-setup/:token" element={<InviteSetup />} />

        {/* Protected admin routes */}
        <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/app/dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="appointments" element={<AppointmentsCalendar />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:email" element={<ClientDetail />} />
          <Route path="calendars" element={<Calendars />} />
          <Route path="calendars/create" element={<CalendarCreate />} />
          <Route path="calendars/:id/edit" element={<CalendarEdit />} />
          <Route path="team" element={<Team />} />
          <Route path="organization" element={<OrganizationPage />} />
          <Route path="billing" element={<Billing />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="email-templates" element={<EmailTemplates />} />
          <Route path="personalization" element={<Personalization />} />
          <Route path="pos" element={<POSDashboard />} />
          <Route path="tax" element={<TaxSettings />} />
          <Route path="forecasting" element={<Forecasting />} />
          <Route path="reseller" element={<ResellerProgram />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="guide" element={<Guide />} />
        </Route>

        {/* Platform Admin Routes */}
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<Navigate to="/admin/dashboard" />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="organizations" element={<AdminOrganizations />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="subscriptions" element={<AdminSubscriptions />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="network" element={<AdminNetwork />} />
          <Route path="errors" element={<AdminErrors />} />
          <Route path="activity" element={<AdminActivityLog />} />
          <Route path="security" element={<AdminSecurity />} />
          <Route path="profile" element={<AdminProfile />} />
          <Route path="storage" element={<AdminStorage />} />
          <Route path="database" element={<AdminDatabase />} />
          <Route path="traffic" element={<AdminTraffic />} />
          <Route path="resources" element={<AdminResources />} />
          <Route path="api-keys" element={<AdminApiKeys />} />
          <Route path="webhooks" element={<AdminWebhooks />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          <Route path="backup" element={<AdminBackup />} />
          <Route path="feature-flags" element={<AdminFeatureFlags />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="audit-logs" element={<AdminAuditLogs />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="maintenance" element={<AdminMaintenance />} />
          <Route path="gdpr" element={<AdminGDPR />} />
          <Route path="logs" element={<AdminLogsViewer />} />
          <Route path="cron" element={<AdminCronJobs />} />
          <Route path="cache" element={<AdminCache />} />
          <Route path="email" element={<AdminEmailDelivery />} />
          <Route path="roles" element={<AdminUserRoles />} />
          <Route path="invoices" element={<AdminBillingInvoices />} />
          <Route path="localization" element={<AdminLocalization />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Org Panel Routes */}
        <Route path="/org/*" element={
          <OrgProvider>
            <OrgRoute />
          </OrgProvider>
        }>
          <Route index element={<Navigate to="/org/overview" />} />
          <Route path="overview" element={<OrgPanel />} />
          <Route path="members" element={<OrgAdminRoute><OrgMembers /></OrgAdminRoute>} />
          <Route path="calendars" element={<OrgCalendars />} />
          <Route path="calendars/create" element={<OrgRoute requiredRole="member"><OrgCalendarCreate /></OrgRoute>} />
          <Route path="calendars/:id/edit" element={<OrgRoute requiredRole="member"><OrgCalendarEdit /></OrgRoute>} />
          <Route path="appointments" element={<OrgAppointments />} />
          <Route path="clients" element={<OrgClients />} />
          <Route path="clients/:email" element={<OrgClientDetail />} />
          <Route path="email-templates" element={<OrgAdminRoute><OrgEmailTemplates /></OrgAdminRoute>} />
          <Route path="personalization" element={<OrgAdminRoute><OrgPersonalization /></OrgAdminRoute>} />
          <Route path="pos" element={<OrgPOS />} />
          <Route path="transactions" element={<OrgTransactions />} />
          <Route path="tax" element={<OrgAdminRoute><OrgTaxSettings /></OrgAdminRoute>} />
          <Route path="forecasting" element={<OrgForecasting />} />
          <Route path="reseller" element={<OrgReseller />} />
          <Route path="reviews" element={<OrgReviews />} />
          <Route path="guide" element={<OrgGuide />} />
          <Route path="team" element={<OrgAdminRoute><OrgTeam /></OrgAdminRoute>} />
          <Route path="organization" element={<OrgOwnerRoute><OrgOrganizationSettings /></OrgOwnerRoute>} />
          <Route path="profile" element={<OrgUserProfile />} />
          <Route path="billing" element={<OrgOwnerRoute><OrgBilling /></OrgOwnerRoute>} />
          <Route path="analytics" element={<OrgAnalytics />} />
          <Route path="integrations" element={<OrgAdminRoute><OrgIntegrations /></OrgAdminRoute>} />
          <Route path="settings" element={<OrgOwnerRoute><OrgSettings /></OrgOwnerRoute>} />
        </Route>

        {/* Legacy routes redirect to /app */}
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" />} />
        <Route path="/calendars" element={<Navigate to="/app/calendars" />} />
        <Route path="/team" element={<Navigate to="/app/team" />} />
        <Route path="/profile" element={<Navigate to="/app/profile" />} />
        <Route path="/settings" element={<Navigate to="/app/settings" />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
        </Suspense>
    </TooltipProvider>
  </ErrorBoundary>
  );
}
